/**
 * VehicleUsagesTab — l'usage du véhicule dans le temps (lot 2 « le temps », §2.3)
 *
 * Trois sections : En cours / À venir / Historique. Chaque ligne = un conducteur,
 * un créneau, une U.T. (le site) et des kilomètres.
 *
 * ⚠️ DEUX CHOSES À NE PAS « AMÉLIORER » :
 *
 * 1. `site_id === null` s'affiche « rattachement inconnu ». 🚫 Ne JAMAIS le
 *    remplacer par le site actuel du véhicule : ce serait présenter l'attache
 *    d'aujourd'hui comme celle du jour de la garde.
 * 2. `km === null` s'affiche « — ». 🚫 Jamais « 0 km » : un zéro se lit
 *    « n'a pas roulé », ce qui est une autre affirmation que « on ne sait pas ».
 *
 * Toutes les chaînes passent par `t('vehicleUsages.…')` — aucun français en dur.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Clock, CalendarClock, History, AlertTriangle, MapPin, User } from 'lucide-react';
import { vehicleUsagesAPI } from '../../../api/vehicles.js';
import { useWorkingSites } from '../../../hooks/useDataHooks.js';
import { useFormat } from '../../../i18n/format.js';

/** Une ligne d'usage. `withKm` distingue une garde vécue d'une garde à venir. */
function UsageRow({ usage, siteLabel, withKm, t, fmt }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 py-3 px-4 border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
      <div className="flex items-center gap-2 min-w-0">
        <User className="w-4 h-4 text-gray-400 shrink-0" aria-hidden="true" />
        <span className="text-sm font-medium text-gray-900 truncate">
          {usage.driver_name || t('vehicleUsages.unknownDriver')}
        </span>
      </div>

      <div className="text-sm text-gray-600 whitespace-nowrap">
        {usage.planned_start ? fmt.dateTime(usage.planned_start) : '—'}
        {' → '}
        {usage.planned_end ? fmt.dateTime(usage.planned_end) : '—'}
      </div>

      <div className="flex items-center gap-1 text-sm text-gray-600 min-w-0">
        <MapPin className="w-4 h-4 text-gray-400 shrink-0" aria-hidden="true" />
        <span className="truncate">{siteLabel}</span>
      </div>

      {withKm && (
        <div className="text-sm text-gray-900 tabular-nums whitespace-nowrap">
          {usage.km === null || usage.km === undefined
            ? <span className="text-gray-400" title={t('vehicleUsages.kmUnknownHint')}>—</span>
            : t('vehicleUsages.kmValue', { km: fmt.number(usage.km) })}
        </div>
      )}
    </div>
  );
}

function Section({ icon: Icon, title, count, children }) {
  return (
    <section className="bg-white rounded-lg shadow-sm">
      <header className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
        <Icon className="w-4 h-4 text-gray-500" aria-hidden="true" />
        <h3 className="text-sm font-bold text-gray-900">{title}</h3>
        {count > 0 && (
          <span className="ml-auto px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full">{count}</span>
        )}
      </header>
      {children}
    </section>
  );
}

export function VehicleUsagesTab({ entity }) {
  const vehicleId = entity?.id;
  const { t } = useTranslation();
  const { fmt } = useFormat();
  const { data: sites = [] } = useWorkingSites();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['vehicleUsages', vehicleId],
    queryFn: () => vehicleUsagesAPI.getByVehicle(vehicleId),
    enabled: !!vehicleId,
  });

  const siteNameById = React.useMemo(() => {
    const map = new Map();
    for (const s of sites) map.set(Number(s.id), s.site_name || s.name || null);
    return map;
  }, [sites]);

  // Le nom si on le connaît, l'id sinon — et « rattachement inconnu » quand la
  // réservation ne porte pas de site (cf. note 1 de l'en-tête).
  const siteLabel = (siteId) => {
    if (siteId === null || siteId === undefined) return t('vehicleUsages.unknownSite');
    return siteNameById.get(Number(siteId)) || String(siteId);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">{t('vehicleUsages.loading')}</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-8 bg-red-50 rounded-lg">
        <AlertTriangle className="w-12 h-12 text-red-300 mx-auto mb-2" aria-hidden="true" />
        <p className="text-red-600">{error?.message || t('vehicleUsages.error')}</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
        >
          {t('vehicleUsages.retry')}
        </button>
      </div>
    );
  }

  const usages = data?.usages || {};
  const current = usages.current || null;
  const upcoming = usages.upcoming || [];
  const past = usages.past || [];
  const truncated = data?.coverage?.truncated === true;

  if (!current && upcoming.length === 0 && past.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm py-12 text-center">
        <Clock className="w-12 h-12 text-gray-300 mx-auto mb-2" aria-hidden="true" />
        <p className="text-gray-600">{t('vehicleUsages.empty')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Section icon={Clock} title={t('vehicleUsages.current')} count={current ? 1 : 0}>
        {current ? (
          <UsageRow usage={current} siteLabel={siteLabel(current.site_id)} withKm t={t} fmt={fmt} />
        ) : (
          <p className="px-4 py-4 text-sm text-gray-500">{t('vehicleUsages.noCurrent')}</p>
        )}
      </Section>

      <Section icon={CalendarClock} title={t('vehicleUsages.upcoming')} count={upcoming.length}>
        {upcoming.length ? upcoming.map((u) => (
          <UsageRow key={u.reservation_id} usage={u} siteLabel={siteLabel(u.site_id)} withKm={false} t={t} fmt={fmt} />
        )) : (
          <p className="px-4 py-4 text-sm text-gray-500">{t('vehicleUsages.noUpcoming')}</p>
        )}
      </Section>

      <Section icon={History} title={t('vehicleUsages.past')} count={past.length}>
        {past.length ? past.map((u) => (
          <UsageRow key={u.reservation_id} usage={u} siteLabel={siteLabel(u.site_id)} withKm t={t} fmt={fmt} />
        )) : (
          <p className="px-4 py-4 text-sm text-gray-500">{t('vehicleUsages.noPast')}</p>
        )}
      </Section>

      {/* Un historique tronqué se DIT — l'utilisateur doit savoir qu'il ne voit pas tout. */}
      {truncated && (
        <p className="text-xs text-amber-700 bg-amber-50 rounded px-3 py-2">
          {t('vehicleUsages.truncated')}
        </p>
      )}
    </div>
  );
}

export default VehicleUsagesTab;
